import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { reads } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { addObserver, removeObserver } from '@ember/object/observers';
import { next } from '@ember/runloop';

import FunctionalUtils from 'mewe/shared/functional-utils';
import PS from 'mewe/utils/pubsub';
import ChatApi from 'mewe/api/chat-api';
import ChatStore from 'mewe/stores/chat-store';
import dispatcher from 'mewe/dispatcher';

export default class MwEventSettingsChat extends Component {
  @reads('args.eventData') eventData;

  @service dynamicDialogs;
  @service account;

  @tracked loaded;
  @tracked chatMode;
  @tracked isSavingChatMode;

  constructor() {
    super(...arguments);

    addObserver(this, 'eventData.participationType', this, this.eventDataReady);

    if (this.account.activeUser?.id && this.eventData?.participationType) {
      this.eventDataReady();
    }
  }

  willDestroy() {
    removeObserver(this, 'eventData.participationType', this, this.eventDataReady);
    super.willDestroy(...arguments);
  }

  eventDataReady() {
    if (!this.loaded && this.eventData?.participationType) {
      next(() => {
        this.chatMode = this.eventData.chatMode;
        this.loaded = true;
      });
    }
  }

  @computed('chatMode', 'isSavingChatMode')
  get isSaveVisible() {
    const isChatVisibilityChanged = this.chatMode !== this.eventData.chatMode;
    return isChatVisibilityChanged && !this.isSavingChatMode;
  }

  @action
  showDeleteConfirmation() {
    this.dynamicDialogs.openDialog('confirm-deletion-dialog', {
      onConfirm: () => this.submitDeleting(),
    });
  }

  @action
  chatModeChange(chatMode) {
    this.chatMode = chatMode;
  }

  @action
  saveChanges() {
    const chatMode = this.chatMode;
    const eventId = this.eventData.id;

    this.isSavingChatMode = true;

    ChatApi.setEventChatMode(eventId, { chatMode: chatMode })
      .then(() => {
        FunctionalUtils.info(__('Chat Settings have been successfully saved.'));

        dispatcher.dispatch('events', 'saveEventState', 'rootEvent.chatMode', chatMode, eventId);

        if (chatMode === 'off') {
          ChatStore.send('removeThreadById', eventId);
        }
      })
      .catch(() => {
        this.chatMode = this.eventData.chatMode;
      })
      .finally(() => {
        this.isSavingChatMode = false;
      });
  }

  @action
  submitDeleting() {
    this.isDeletingHistory = true;

    // delete event chat history form
    ChatApi.deleteEventChatHistory(this.eventData.id)
      .then(() => {
        FunctionalUtils.info(__('Event chat history has been successfully deleted.'));
        PS.Pub('chat.remove.all.messages', {
          threadId: this.eventData.id,
        });
      })
      .catch(() => {
        FunctionalUtils.showDefaultErrorMessage();
      })
      .finally(() => {
        this.isDeletingHistory = false;
      });
  }
}
